import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { NEWSLETTER_TYPES, LEAD_MAGNET_TYPES } from "@/lib/generateConstants";

export function useHistoryActions({ api, fetchHistory }) {
  const navigate = useNavigate();

  const [deleteId, setDeleteId] = useState(null);
  const [scoringId, setScoringId] = useState(null);
  const [scoreModal, setScoreModal] = useState(null);
  const [draftPreview, setDraftPreview] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);
  const [repurposeItem, setRepurposeItem] = useState(null);
  const [approvalModal, setApprovalModal] = useState(null);

  const handleDelete = useCallback(async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/history/${deleteId}`);
      toast.success("Deleted");
      setDeleteId(null);
      fetchHistory();
    } catch { toast.error("Failed to delete"); }
  }, [api, deleteId, fetchHistory]);

  const handleToggleFavorite = useCallback(async (item) => {
    try {
      const resp = await api.post(`/history/${item.history_id}/favorite`);
      toast.success(resp.data.is_favorited ? "Added to favorites" : "Removed from favorites");
      return resp.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update favorite");
      return null;
    }
  }, [api]);

  const handleTogglePin = useCallback(async (item) => {
    try {
      const resp = await api.post(`/history/${item.history_id}/pin`);
      toast.success(resp.data.is_pinned ? "Pinned to top" : "Unpinned");
      fetchHistory();
      return resp.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update pin");
      return null;
    }
  }, [api, fetchHistory]);

  const handleStatusChange = useCallback(async (item, newStatus) => {
    setStatusDropdownOpen(null);
    try {
      await api.patch(`/history/${item.history_id}/status`, { status: newStatus });
      return newStatus;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
      return null;
    }
  }, [api]);

  const handlePublishDate = useCallback(async (item, dateStr) => {
    const val = dateStr || null;
    try {
      await api.patch(`/history/${item.history_id}/publish-date`, { planned_publish_date: val });
      toast.success(val ? "Publish date set" : "Publish date cleared");
      return val;
    } catch {
      toast.error("Failed to update date");
      return undefined;
    }
  }, [api]);

  const scoreContent = useCallback(async (historyId) => {
    setScoringId(historyId);
    try {
      const resp = await api.post("/score-content", { history_id: historyId });
      setScoreModal(resp.data);
      if (!resp.data.cached) toast.success(`Scored: ${resp.data.quality_score}/10`);
      return resp.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to score content");
      return null;
    } finally {
      setScoringId(null);
    }
  }, [api]);

  const handleDiscardDraft = useCallback(async (draftId) => {
    try {
      await api.delete(`/history/draft/${draftId}`);
      toast.success("Draft discarded");
      return true;
    } catch {
      toast.error("Failed to discard draft");
      return false;
    }
  }, [api]);

  const handleResumeDraft = useCallback((draft) => {
    const ct = draft.content_type || "";
    const state = { resumeDraft: draft };
    if (NEWSLETTER_TYPES.includes(ct)) navigate("/newsletter", { state });
    else if (LEAD_MAGNET_TYPES.includes(ct)) navigate("/lead-magnet", { state });
    else navigate("/generate", { state });
  }, [navigate]);

  const handleSaveDraftToHistory = useCallback(async (draft) => {
    setSavingDraft(true);
    try {
      const sectionEntries = Object.entries(draft.sections || {});
      const markdown = sectionEntries.map(([name, content]) => `### ${name}\n\n${content}`).join("\n\n---\n\n");
      const ctx = draft.generation_context || {};
      await api.post("/history", {
        title: ctx.topic || draft.content_type || "Recovered Draft",
        topic: ctx.topic || draft.content_type || "",
        content_type: draft.content_type || "",
        platform: ctx.platform || "",
        audience: ctx.audience || "",
        goal: ctx.goal || "",
        cta: ctx.cta || "NONE",
        offer_asset: "NONE",
        tone_variations: ctx.tone_variations || [],
        length_constraint: "FULL LENGTH",
        ai_model_used: ctx.ai_model || "",
        generated_draft: markdown,
      });
      await api.delete(`/history/draft/${draft.draft_id}`);
      setDraftPreview(null);
      toast.success("Draft saved to history!");
      fetchHistory();
      return true;
    } catch {
      toast.error("Failed to save draft to history");
      return false;
    } finally {
      setSavingDraft(false);
    }
  }, [api, fetchHistory]);

  const handleDuplicate = useCallback((item) => {
    const prefill = { ...item, duplicate: true };
    if (NEWSLETTER_TYPES.includes(item.content_type)) navigate("/newsletter", { state: { prefill } });
    else if (LEAD_MAGNET_TYPES.includes(item.content_type)) navigate("/lead-magnet", { state: { prefill } });
    else navigate("/generate", { state: { prefill } });
  }, [navigate]);

  const loadToEditor = useCallback((item) => {
    navigate("/generate", { state: { prefill: item } });
  }, [navigate]);

  return {
    deleteId, setDeleteId, handleDelete,
    handleToggleFavorite, handleTogglePin,
    statusDropdownOpen, setStatusDropdownOpen, handleStatusChange,
    handlePublishDate,
    scoringId, scoreContent, scoreModal, setScoreModal,
    draftPreview, setDraftPreview, savingDraft,
    handleDiscardDraft, handleResumeDraft, handleSaveDraftToHistory,
    repurposeItem, setRepurposeItem,
    approvalModal, setApprovalModal,
    handleDuplicate, loadToEditor,
  };
}
